import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { BookOpen, ArrowLeft, MessageSquare, User, Clock, ChevronDown, ChevronUp, Reply } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export default function CourseDiscussions({ user }) {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [discussions, setDiscussions] = useState([]);
  const [activityDiscussions, setActivityDiscussions] = useState({});
  const [activities, setActivities] = useState([]);
  const [expandedLessons, setExpandedLessons] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [courseId]);

  const loadData = async () => {
    try {
      // Load course details
      const courseRes = await fetch(`${API}/courses/${courseId}`, {
        headers: getAuthHeaders()
      });
      if (courseRes.ok) {
        const courseData = await courseRes.json();
        setCourse(courseData);
        // Expand all lessons by default
        const expanded = {};
        (courseData.content || []).forEach((_, idx) => {
          expanded[idx] = true;
        });
        expanded['course'] = true; // Expand course-level discussions
        setExpandedLessons(expanded);
      }

      // Load course-level discussions
      const discussionsRes = await fetch(`${API}/discussions/${courseId}`, {
        headers: getAuthHeaders()
      });
      if (discussionsRes.ok) {
        setDiscussions(await discussionsRes.json());
      }

      // Load activities to get activity discussions
      const activitiesRes = await fetch(`${API}/courses/${courseId}/activities`, {
        headers: getAuthHeaders()
      });
      if (activitiesRes.ok) {
        const activitiesData = await activitiesRes.json();
        setActivities(activitiesData);
        
        // Load discussions for each activity
        const discussionsMap = {};
        for (const activity of activitiesData) {
          const actDiscussionsRes = await fetch(`${API}/activities/${activity.activity_id}/discussions`, {
            headers: getAuthHeaders()
          });
          if (actDiscussionsRes.ok) {
            const actDiscussionsData = await actDiscussionsRes.json();
            const lessonIndex = activity.lesson_index || 0;
            if (!discussionsMap[lessonIndex]) {
              discussionsMap[lessonIndex] = [];
            }
            discussionsMap[lessonIndex].push({
              activity,
              discussions: actDiscussionsData.messages || []
            });
          }
        }
        setActivityDiscussions(discussionsMap);
      }
    } catch (error) {
      toast.error('Failed to load discussions');
    } finally {
      setLoading(false);
    }
  };

  const toggleLessonExpand = (lessonKey) => {
    setExpandedLessons(prev => ({
      ...prev,
      [lessonKey]: !prev[lessonKey]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const organizeDiscussionsByThread = (messages) => {
    // Separate threads (messages with title) from replies
    const threads = messages.filter(m => m.title && !m.parent_id);
    const replies = messages.filter(m => m.parent_id);
    
    // Attach replies to their parent threads
    return threads.map(thread => ({
      ...thread,
      replies: replies.filter(r => r.parent_id === thread.message_id)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAEDCD] flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="w-12 h-12 text-[#588157] mx-auto mb-4 animate-pulse" />
          <p className="text-[#588157]">Loading discussions...</p>
        </div>
      </div>
    );
  }

  const lessonIndices = course?.content ? Object.keys(course.content).map(Number) : [];

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate('/my-courses')}
            data-testid="back-button"
            className="text-[#3A5A40] hover:bg-[#E9EDC9]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            <span className="hidden sm:inline">Back to My Courses</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-[#588157]" strokeWidth={1.5} />
            <h1 className="text-lg sm:text-xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
              Course Discussions
            </h1>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto p-4 sm:p-6 md:p-12">
        {/* Course Header */}
        {course && (
          <div className="mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
              {course.title}
            </h2>
            <p className="text-[#588157]">View all discussions from teachers and students</p>
          </div>
        )}

        <div className="space-y-6 sm:space-y-8">
          {/* Course-Level Discussions */}
          <Card className="bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl overflow-hidden">
            <div 
              className="p-4 bg-[#3A5A40] text-white cursor-pointer flex items-center justify-between"
              onClick={() => toggleLessonExpand('course')}
            >
              <div className="flex items-center gap-3">
                <MessageSquare className="w-6 h-6" />
                <h3 className="text-lg font-bold">Course Discussion Forum</h3>
                <span className="text-sm bg-white/20 px-2 py-0.5 rounded-full">
                  {discussions.length} posts
                </span>
              </div>
              {expandedLessons['course'] ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </div>

            {expandedLessons['course'] && (
              <div className="p-4">
                {discussions.length === 0 ? (
                  <p className="text-[#A3B18A] italic text-center py-4">No discussions yet in the course forum.</p>
                ) : (
                  <div className="space-y-4">
                    {organizeDiscussionsByThread(discussions).map((thread) => (
                      <div key={thread.message_id} className="border border-[#CCD5AE]/50 rounded-lg overflow-hidden">
                        {/* Thread Header */}
                        <div className="p-4 bg-white">
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                              thread.user_role === 'teacher' ? 'bg-[#3A5A40]' : 'bg-[#D4A373]'
                            }`}>
                              {thread.user_name?.[0] || '?'}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-bold text-[#344E41]">{thread.user_name || 'User'}</span>
                                {thread.user_role === 'teacher' && (
                                  <span className="text-xs bg-[#3A5A40] text-white px-2 py-0.5 rounded-full">Teacher</span>
                                )}
                              </div>
                              {thread.title && (
                                <h4 className="text-lg font-bold text-[#344E41] mb-2">{thread.title}</h4>
                              )}
                              <p className="text-[#344E41]">{thread.content || thread.message}</p>
                              <p className="text-xs text-[#A3B18A] mt-2">
                                <Clock className="w-3 h-3 inline mr-1" />
                                {formatDate(thread.created_at)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Replies */}
                        {thread.replies && thread.replies.length > 0 && (
                          <div className="bg-[#F5F0DC] p-4 border-t border-[#CCD5AE]/50">
                            <div className="flex items-center gap-2 mb-3 text-sm text-[#588157]">
                              <Reply className="w-4 h-4" />
                              {thread.replies.length} {thread.replies.length === 1 ? 'reply' : 'replies'}
                            </div>
                            <div className="space-y-3 ml-4">
                              {thread.replies.map((reply) => (
                                <div key={reply.message_id} className="flex items-start gap-3 p-3 bg-white rounded-lg">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                    reply.user_role === 'teacher' ? 'bg-[#3A5A40]' : 'bg-[#D4A373]'
                                  }`}>
                                    {reply.user_name?.[0] || '?'}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-medium text-sm text-[#344E41]">{reply.user_name || 'User'}</span>
                                      {reply.user_role === 'teacher' && (
                                        <span className="text-xs bg-[#3A5A40] text-white px-1.5 py-0.5 rounded-full">Teacher</span>
                                      )}
                                    </div>
                                    <p className="text-sm text-[#344E41]">{reply.content || reply.message}</p>
                                    <p className="text-xs text-[#A3B18A] mt-1">
                                      {formatDate(reply.created_at)}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  onClick={() => navigate(`/courses/${courseId}/view`)}
                  className="mt-4 bg-[#3A5A40] text-white hover:bg-[#344E41]"
                >
                  Go to Course to Participate
                </Button>
              </div>
            )}
          </Card>

          {/* Lesson-Level Discussions (Activity Discussions) */}
          {lessonIndices.map((lessonIndex) => {
            const lessonTitle = course?.content?.[lessonIndex]?.title || `Lesson ${lessonIndex + 1}`;
            const lessonActivityDiscussions = activityDiscussions[lessonIndex] || [];
            const totalPosts = lessonActivityDiscussions.reduce((sum, ad) => sum + ad.discussions.length, 0);

            return (
              <Card key={lessonIndex} className="bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl overflow-hidden">
                <div 
                  className="p-4 bg-[#588157] text-white cursor-pointer flex items-center justify-between"
                  onClick={() => toggleLessonExpand(lessonIndex)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center font-bold">
                      {lessonIndex + 1}
                    </div>
                    <h3 className="text-lg font-bold">{lessonTitle}</h3>
                    <span className="text-sm bg-white/20 px-2 py-0.5 rounded-full">
                      {totalPosts} posts
                    </span>
                  </div>
                  {expandedLessons[lessonIndex] ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </div>

                {expandedLessons[lessonIndex] && (
                  <div className="p-4">
                    {lessonActivityDiscussions.length === 0 ? (
                      <p className="text-[#A3B18A] italic text-center py-4">No activity discussions for this lesson.</p>
                    ) : (
                      <div className="space-y-4">
                        {lessonActivityDiscussions.map(({ activity, discussions: actDiscussions }) => (
                          <div key={activity.activity_id} className="border border-[#CCD5AE]/50 rounded-lg overflow-hidden">
                            {/* Activity Header */}
                            <div className="p-3 bg-[#E9EDC9]">
                              <div className="flex items-center gap-2">
                                <span className="text-xs bg-[#D4A373] text-white px-2 py-0.5 rounded-full">
                                  Day {activity.day_number}
                                </span>
                                <h4 className="font-bold text-[#344E41]">{activity.title}</h4>
                              </div>
                            </div>

                            {/* Activity Discussions */}
                            <div className="p-4 bg-white">
                              {actDiscussions.length === 0 ? (
                                <p className="text-[#A3B18A] italic text-sm">No responses yet for this activity.</p>
                              ) : (
                                <div className="space-y-3">
                                  {actDiscussions.map((discussion) => (
                                    <div key={discussion.message_id} className="flex items-start gap-3 p-3 bg-[#FAEDCD] rounded-lg">
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                        discussion.user_role === 'teacher' ? 'bg-[#3A5A40]' : 'bg-[#D4A373]'
                                      }`}>
                                        {discussion.user_name?.[0] || '?'}
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="font-medium text-sm text-[#344E41]">{discussion.user_name || 'User'}</span>
                                          {discussion.user_role === 'teacher' && (
                                            <span className="text-xs bg-[#3A5A40] text-white px-1.5 py-0.5 rounded-full">Teacher</span>
                                          )}
                                          {discussion.user_id === user?.user_id && (
                                            <span className="text-xs bg-[#3A5A40]/20 text-[#3A5A40] px-1.5 py-0.5 rounded-full">You</span>
                                          )}
                                        </div>
                                        <p className="text-sm text-[#344E41]">{discussion.message}</p>
                                        <p className="text-xs text-[#A3B18A] mt-1">
                                          <Clock className="w-3 h-3 inline mr-1" />
                                          {formatDate(discussion.created_at)}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
}
